import { type FormEvent, useCallback, useEffect, useState } from 'react';

import {
  type AdsCreativeRequest,
  type BrandBookSummary,
  type Content,
  type ContentAddress,
  type ContentInput,
  type ContentKind,
  contentPath,
  createContent,
  describeFormatPreset,
  fetchAdsCreativeRequest,
  fetchContentList,
  fetchPresets,
  fetchBrandBookProfile,
  fetchBrandBooks,
  fetchSettings,
  fetchVideoModels,
  type FormatPreset,
  isDutchLanguage,
  refreshBrandBookBrand,
  type Settings,
  type ToneOption,
  type VideoCatalogueView,
  startAdsCreativeRequest,
} from '../api.ts';
import {
  briefFromRequest,
  contentKindOfRequest,
  contentNameFromConcept,
  presetsForRatios,
} from '../creativeRequestBrief.ts';
import {
  BriefDetailsFields,
  type BriefDetailsDraft,
  detailsOf,
  draftOf,
} from '../BriefDetailsFields.tsx';
import {
  CampaignField,
  FALLBACK_LANGUAGES,
  LanguageField,
  type ProfileLanguage,
  RepositoryChoice,
  useCampaignChoice,
  useRepositoryOrder,
} from '../contentFields.tsx';
import { describeError, redirectIfUnauthorised, useAppData } from '../data.tsx';
import { Icon } from '../Icon.tsx';
import { brandBookPath, Link, navigate } from '../router.tsx';
import { fromLocalParts, normaliseTime } from '../schedule.ts';
import { useToast } from '../toast.tsx';
import { Badge, Loader, Notice, PageHeader, Panel, Skeleton } from '../ui.tsx';
import * as wording from '../wording.ts';
import { brandBookSource, NewBrandBookDialog } from './BrandBooks.tsx';

/**
 * Creating a content: the form on the left, what happens to it on the right.
 *
 * Its own page rather than a dialog over the list, so the URL can be handed
 * out and `g n` has somewhere to go. Everything a content is decided by is
 * chosen here, because the planning conversation that follows reads all of it
 * and cannot ask again: the name becomes the workspace directory and the
 * container, the formats become the render targets, and the tone selects the
 * rules file every later probe measures against.
 *
 * The vocabulary is the server's, never this file's — the tones and their
 * one-line summaries come with `GET /api/contents`, and the presets with their
 * sizes and platform limits come from `GET /api/presets`, so the form cannot
 * offer something the server would refuse.
 */

/** The name becomes a directory, a container name and a URL segment. */
const CONTENT_NAME_PATTERN = /^[A-Za-z0-9_-]+$/;
const MAX_NAME_LENGTH = 60;
/** The server's ceiling on a content's own cap. */
const MAX_SPEND_CAP_USD = 10_000;

/** The server's window on a video's target length, in whole seconds (US-089). */
const MIN_VIDEO_SECONDS = 5;
const MAX_VIDEO_SECONDS = 90;

/** A fresh, untouched Brief details section: nothing filled in. */
const NO_BRIEF_DETAILS = draftOf({
  goal: null,
  audience: null,
  angle: null,
  placement: null,
  funnelStages: [],
  placed: false,
  extraDetails: [],
});

/** The Defaults toggles, in the order the panel and this form ask them. */
const TOGGLES = [
  {
    field: 'captions',
    label: 'Captions',
    hint: 'Burned-in captions, so the words land in a muted feed.',
    video: true,
  },
  {
    field: 'music',
    label: 'Music',
    hint: 'A music bed under the cut, ducked beneath any voice.',
    video: true,
  },
  {
    field: 'voiceover',
    label: 'Narrator (voice-over)',
    hint: 'A voice over the pictures. Characters who speak in the script are always voiced, whatever this says.',
    video: true,
  },
  {
    field: 'approveScript',
    label: 'Approve the narration',
    hint: 'The words are approved in the planning conversation. On, the production also stops at the script for a second read before it is spoken.',
    video: true,
  },
  {
    field: 'watermark',
    label: 'Watermark / logo bug',
    hint: 'The brand mark in a corner of every frame.',
    video: false,
  },
  {
    field: 'autopilot',
    label: 'Plan and produce without waiting for me',
    hint: 'Autopilot: this content plans and produces by itself and skips the narration stop. Take over on its page to get the stops back.',
    video: false,
  },
] as const;

type ToggleField = (typeof TOGGLES)[number]['field'];

/** How often the brand books are re-read while the chosen one is extracting. */
const BRAND_BOOK_POLL_MS = 3000;

/** Where the language comes from: the brand book here, not a repository. */
const LANGUAGE_HINT =
  'What the script is written in and the voice speaks. Filled in from the brand book’s product ' +
  'profile when it has one, otherwise from Settings → Defaults.';

export function NewContent() {
  const { repositories, refresh } = useAppData();
  const toast = useToast();

  const [kind, setKind] = useState<ContentKind>('video');
  const [name, setName] = useState('');
  /** Selected repositories, in the order they were picked: the first is primary. */
  const {
    chosen: chosenRepositories,
    toggle: toggleRepository,
    makePrimary,
  } = useRepositoryOrder();
  const [formats, setFormats] = useState<string[]>([]);
  const [tone, setTone] = useState('professional');
  /**
   * The content's language (synthesis C2): the brand book's product profile,
   * then Settings → Defaults, until the operator picks one — after that it is
   * theirs and a change of brand book no longer moves it.
   */
  const [language, setLanguage] = useState('en-US');
  const [address, setAddress] = useState<ContentAddress | ''>('');
  const [languageChosen, setLanguageChosen] = useState(false);
  const [languages, setLanguages] = useState<readonly string[]>(FALLBACK_LANGUAGES);
  const [creativeDirection, setCreativeDirection] = useState('');
  const [brief, setBrief] = useState('');
  /** The optional brief details (US-004); sent only when something is filled in. */
  const [briefDetails, setBriefDetails] = useState<BriefDetailsDraft>(NO_BRIEF_DETAILS);
  /** Text rather than a number: an empty field means "no cap of its own". */
  /** Text, like the cap: the field is only read when the kind is video. */
  const [targetSeconds, setTargetSeconds] = useState('');
  const [toggles, setToggles] = useState<Record<ToggleField, boolean>>({
    captions: true,
    music: true,
    voiceover: true,
    // Off, like the server's default: the words are approved in planning.
    approveScript: false,
    watermark: false,
    // On, like the server's default (one-shot ads US-020).
    autopilot: true,
  });
  // The per-content video model (US-085). '' is "follow the plan and this
  // install", which is what almost every content does.
  const [videoModel, setVideoModel] = useState('');
  const [catalogue, setCatalogue] = useState<VideoCatalogueView | null>(null);
  const [spendCap, setSpendCap] = useState('');
  const [schedule, setSchedule] = useState(false);
  const [day, setDay] = useState('');
  const [time, setTime] = useState('');
  /** The brand book the content is made in: required, and the only brand it has. */
  const [brandBookId, setBrandBookId] = useState('');
  const [brandBooks, setBrandBooks] = useState<BrandBookSummary[] | null>(null);
  /** The board fields new work can start with (US-030): a campaign and a publish-by date. */
  const campaignChoice = useCampaignChoice();
  const [dueOn, setDueOn] = useState('');
  const [creatingBook, setCreatingBook] = useState(false);
  /** Books whose first extraction this page started that the list may not show yet. */
  const [startingBooks, setStartingBooks] = useState<ReadonlySet<string>>(new Set());

  const [presets, setPresets] = useState<FormatPreset[] | null>(null);
  const [tones, setTones] = useState<ToneOption[] | null>(null);
  /** Settings → Defaults, held until the vocabulary it names has arrived. */
  const [defaults, setDefaults] = useState<Settings | null>(null);
  const [prefilled, setPrefilled] = useState(false);
  const [taken, setTaken] = useState<readonly Content[]>([]);
  const [error, setError] = useState<string | null>(null);
  /** git's own output from a failed clone, shown under the form that made it. */
  const [stderr, setStderr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  // An ads creative request this content is made for (meta-ads-agent US-019):
  // `?creativeRequest=<id>` pre-fills the form once, and the request is started
  // with the content once it exists.
  const [requestId] = useState(() => new URLSearchParams(window.location.search).get('creativeRequest'));
  const [request, setRequest] = useState<AdsCreativeRequest | null>(null);
  const [requestApplied, setRequestApplied] = useState(false);

  // The vocabulary and the default cap, read once: none of it changes while
  // the page is open, because all three are settled at the server's boot or in
  // a settings row nobody else is editing mid-form.
  useEffect(() => {
    const controller = new AbortController();
    fetchPresets(controller.signal)
      .then(setPresets)
      .catch((cause: unknown) => {
        if (!controller.signal.aborted) setError(describeError(cause));
      });
    fetchContentList(controller.signal)
      .then((list) => {
        setTones(list.tones);
        setTaken(list.contents);
        if (list.languages !== undefined && list.languages.length > 0) setLanguages(list.languages);
      })
      .catch((cause: unknown) => {
        if (!controller.signal.aborted) setError(describeError(cause));
      });
    // The video catalogue fills the model picker (US-085). It is a network
    // read on the server's side, so a failure leaves the picker empty and the
    // field below falls back to typing an id — never to blocking the form.
    fetchVideoModels(controller.signal)
      .then(setCatalogue)
      .catch(() => undefined);
    // The cap is a convenience: an unreadable setting leaves the field empty
    // and the content is created without a cap of its own.
    fetchSettings(controller.signal)
      .then((settings) => {
        setSpendCap((current) => (current === '' ? String(settings.defaultSpendCapUsd) : current));
        setDefaults(settings);
      })
      .catch(() => undefined);
    return () => {
      controller.abort();
    };
  }, []);

  // Settings → Defaults, applied once, and only when the vocabulary they name
  // has arrived: a format preset or a tone may have been deleted in the rules
  // editor since it was saved, and a default that no longer exists is dropped
  // rather than pre-filled into a form the server would then refuse.
  //
  // Once, because after that the fields are the operator's: re-applying them
  // would undo a choice made while the page was open. And only ever *here* —
  // nothing writes a default onto a content that already exists.
  useEffect(() => {
    if (prefilled || defaults === null || presets === null || tones === null) return;
    setPrefilled(true);
    setFormats((current) =>
      current.length > 0
        ? current
        : defaults.defaultFormats.filter((name) =>
            presets.some((preset) => preset.preset === name),
          ),
    );
    if (tones.some((option) => option.tone === defaults.defaultTone)) {
      setTone(defaults.defaultTone);
    }
    setTargetSeconds((current) =>
      current === '' ? String(defaults.defaultVideoSeconds) : current,
    );
    setToggles({
      captions: defaults.defaultCaptions,
      music: defaults.defaultMusic,
      voiceover: defaults.defaultVoiceover,
      approveScript: defaults.defaultApproveScript,
      watermark: defaults.defaultWatermark,
      autopilot: defaults.defaultAutopilot,
    });
  }, [defaults, prefilled, presets, tones]);

  useEffect(() => {
    if (requestId === null) return;
    const controller = new AbortController();
    fetchAdsCreativeRequest(requestId, controller.signal)
      .then((found) => {
        setRequest(found.request);
      })
      .catch((cause: unknown) => {
        if (!controller.signal.aborted) setError(describeError(cause));
      });
    return () => {
      controller.abort();
    };
  }, [requestId]);

  // Applied once, after the presets have arrived (the ratios are matched to
  // them) and on top of the Defaults: what the agent asked for wins over the
  // usual formats, and after this the fields are the operator's.
  useEffect(() => {
    if (requestApplied || request === null || presets === null) return;
    setRequestApplied(true);
    const requestedKind = contentKindOfRequest(request) ?? kind;
    setKind(requestedKind);
    setName((current) => (current === '' ? contentNameFromConcept(request.concept) : current));
    setBrief((current) => (current === '' ? briefFromRequest(request) : current));
    setBriefDetails((current) => ({
      ...current,
      angle: current.angle === '' ? (request.angle ?? '') : current.angle,
      placement: current.placement === '' ? 'Meta ads (Facebook and Instagram)' : current.placement,
    }));
    const matched = presetsForRatios(presets, request.aspectRatios, requestedKind);
    setFormats((current) =>
      matched.length > 0
        ? matched
        : current.filter((preset) =>
            presets.some((known) => known.preset === preset && known.kinds.includes(requestedKind)),
          ),
    );
  }, [kind, presets, request, requestApplied]);

  // The brand books, the shell's poll does not carry them. Read on arrival and
  // again every few seconds while the chosen one is extracting, so the notice
  // under the select moves on to ready (or failed) without a reload.
  const loadBrandBooks = useCallback(async (signal?: AbortSignal): Promise<void> => {
    try {
      setBrandBooks(await fetchBrandBooks(signal));
    } catch (cause: unknown) {
      if (signal?.aborted === true || redirectIfUnauthorised(cause)) return;
      setError(describeError(cause));
      // An empty list rather than a form that never stops loading.
      setBrandBooks((current) => current ?? []);
    }
  }, []);
  const chosenBook = (brandBooks ?? []).find((book) => book.id === brandBookId) ?? null;
  const chosenExtracting =
    chosenBook !== null &&
    (chosenBook.brandStatus === 'extracting' || startingBooks.has(chosenBook.id));
  useEffect(() => {
    if (brandBooks !== null && !chosenExtracting) return;
    const controller = new AbortController();
    const timer = window.setTimeout(
      () => {
        void loadBrandBooks(controller.signal);
      },
      brandBooks === null ? 0 : BRAND_BOOK_POLL_MS,
    );
    return () => {
      window.clearTimeout(timer);
      controller.abort();
    };
  }, [loadBrandBooks, brandBooks, chosenExtracting]);

  /**
   * A book made with the shortcut is selected at once; a repository or website
   * book also starts its first extraction, as it does on the brand books page.
   * The request answers only when the run is done, so it is not waited on.
   */
  const onBookCreated = (book: BrandBookSummary): void => {
    setCreatingBook(false);
    setBrandBooks((current) =>
      [...(current ?? []).filter((other) => other.id !== book.id), book].sort((a, b) =>
        a.name.localeCompare(b.name, undefined, { sensitivity: 'base' }),
      ),
    );
    setBrandBookId(book.id);
    toast.ok(`Created ${book.name}.`);
    if (book.sourceType === 'manual') return;
    setStartingBooks((current) => new Set(current).add(book.id));
    refreshBrandBookBrand(book.id)
      .then((result) => {
        if (result.status === 'failed') toast.error(`${book.name}: ${result.error ?? result.message}`);
        else toast.ok(`${book.name}: brand profile extracted.`);
      })
      .catch((cause: unknown) => {
        if (redirectIfUnauthorised(cause)) return;
        toast.error(`${book.name}: ${describeError(cause)}`);
      })
      .finally(() => {
        setStartingBooks((current) => {
          const next = new Set(current);
          next.delete(book.id);
          return next;
        });
        void loadBrandBooks();
      });
  };

  // The language follows the product: the brand book's product profile names
  // it, and Settings → Defaults answers when it does not. Only until the
  // operator picks one — a choice made on this page is never overwritten.
  const profileBook = brandBookId === '' ? null : brandBookId;
  useEffect(() => {
    if (languageChosen) return;
    const fallback = defaults?.defaultLanguage ?? 'en-US';
    // No brand book chosen yet: no profile to read a language from.
    if (profileBook === null) {
      setLanguage(fallback);
      setAddress('');
      return;
    }
    const controller = new AbortController();
    fetchBrandBookProfile(profileBook, controller.signal)
      .then((view) => {
        // An answer that arrived just as the operator picked a language (or
        // another brand book) was already queued when the abort came.
        if (controller.signal.aborted) return;
        setLanguage(view.profile.language ?? fallback);
        setAddress(view.profile.address ?? '');
      })
      .catch(() => {
        if (!controller.signal.aborted) setLanguage(fallback);
      });
    return () => {
      controller.abort();
    };
  }, [profileBook, defaults, languageChosen]);
  const languageChoice: ProfileLanguage = {
    language,
    address,
    chooseLanguage: (next) => {
      setLanguage(next);
      setLanguageChosen(true);
    },
    chooseAddress: (next) => {
      setAddress(next);
      setLanguageChosen(true);
    },
  };

  const usable = (repositories ?? []).filter((repository) => repository.keyConfigured);
  // A preset is only offered for the kind it can actually be rendered as.
  const offered = (presets ?? []).filter((preset) => preset.kinds.includes(kind));
  const trimmedName = name.trim();

  const chooseKind = (next: ContentKind): void => {
    setKind(next);
    // Keep only the presets the new kind can be rendered as — a still content
    // has no use for a preset that only exists as video.
    setFormats((current) =>
      current.filter((preset) =>
        (presets ?? []).some((known) => known.preset === preset && known.kinds.includes(next)),
      ),
    );
  };

  const toggleFormat = (preset: string): void => {
    setFormats((current) =>
      current.includes(preset) ? current.filter((entry) => entry !== preset) : [...current, preset],
    );
  };

  const submit = (event: FormEvent): void => {
    event.preventDefault();
    setError(null);
    setStderr(null);

    if (!CONTENT_NAME_PATTERN.test(trimmedName) || trimmedName.length > MAX_NAME_LENGTH) {
      setError(
        `The name may only contain letters, numbers, hyphens and underscores, and at most ${String(MAX_NAME_LENGTH)} of them — it becomes the workspace directory, the container name and the URL.`,
      );
      return;
    }
    if (taken.some((content) => content.name === trimmedName)) {
      setError('There is already a content with this name.');
      return;
    }
    if (chosenBook === null) {
      setError('Choose the brand book this content is made in.');
      return;
    }
    if (formats.length === 0) {
      setError('Choose at least one format preset.');
      return;
    }

    const input: ContentInput = {
      name: trimmedName,
      kind,
      formats,
      tone,
      brandBookId: chosenBook.id,
      repositories: chosenRepositories.map((repositoryId, index) => ({
        repositoryId,
        // The first one picked is what the content is about; the rest are read
        // for context. Exactly one primary is the server's rule as well.
        role: index === 0 ? 'primary' : 'context',
      })),
    };
    // The Defaults panel's answers, as this form left them (US-089). They are
    // sent explicitly rather than left to the server's own defaults, because
    // what the operator saw on screen is what the content has to be made with.
    if (kind === 'video') {
      const seconds = Number(targetSeconds);
      if (
        !Number.isInteger(seconds) ||
        seconds < MIN_VIDEO_SECONDS ||
        seconds > MAX_VIDEO_SECONDS
      ) {
        setError(
          `The target length must be a whole number of seconds between ${String(MIN_VIDEO_SECONDS)} and ${String(MAX_VIDEO_SECONDS)}.`,
        );
        return;
      }
      input.targetSeconds = seconds;
      input.captions = toggles.captions;
      input.music = toggles.music;
      input.voiceover = toggles.voiceover;
      // Only meaningful with a voiceover: nothing narrates, nothing stops.
      input.approveScript = toggles.voiceover && toggles.approveScript;
    }
    input.watermark = toggles.watermark;
    input.autopilot = toggles.autopilot;

    // The language as the operator saw it on screen, and for Dutch the
    // address when one was chosen; an unchosen one is the profile's to give.
    input.language = language;
    if (isDutchLanguage(language) && address !== '') input.address = address;

    // The model override travels only when there is one: the server reads an
    // absent field as "follow the plan and this install" (US-085).
    if (kind === 'video' && videoModel.trim() !== '') input.videoModel = videoModel.trim();

    if (campaignChoice.campaignId !== '') input.campaignId = campaignChoice.campaignId;
    if (dueOn !== '') input.dueOn = dueOn;

    if (brief.trim() !== '') input.brief = brief.trim();
    if (creativeDirection.trim() !== '') input.creativeDirection = creativeDirection.trim();

    // An untouched (or emptied) section sends nothing: the server reads an
    // absent field as the empty set.
    const details = detailsOf(briefDetails);
    if (
      details.goal !== null ||
      details.audience !== null ||
      details.angle !== null ||
      details.placement !== null ||
      details.funnelStages.length > 0 ||
      details.placed ||
      details.extraDetails.length > 0
    ) {
      input.briefDetails = details;
    }

    if (spendCap.trim() !== '') {
      const cap = Number(spendCap);
      if (!Number.isFinite(cap) || cap <= 0 || cap > MAX_SPEND_CAP_USD) {
        setError(
          `The spend cap must be an amount in US dollars between 0 and ${String(MAX_SPEND_CAP_USD)}. Clear it to create the content without a cap of its own.`,
        );
        return;
      }
      input.spendCapUsd = cap;
    }

    if (schedule) {
      const at = fromLocalParts(day, time);
      if (at === null) {
        setError(
          day === ''
            ? 'Pick a day as well as a time.'
            : 'Write the time as HH:mm on a 24-hour clock, such as 07:30.',
        );
        return;
      }
      input.scheduledStartAt = at;
    }

    setBusy(true);
    createContent(input)
      .then(async (created) => {
        if (request !== null && request.status === 'open') {
          await startAdsCreativeRequest(request.id, created.content.id)
            .then((started) => {
              setRequest(started.request);
            })
            .catch((cause: unknown) => {
              toast.warn(`${created.content.name} was created, but the creative request could not be marked started: ${describeError(cause)}`);
            });
        }
        await refresh();
        if (created.setup === null || created.setup.ok) {
          toast.ok(`Created ${created.content.name}.`);
          navigate(contentPath(created.content.id));
          return;
        }
        // The content exists but a clone failed: keep the form, with the values
        // that produced it, and show git's own words underneath.
        setError(created.setup.message);
        setStderr(created.setup.stderr === '' ? null : created.setup.stderr);
        toast.warn(`${created.content.name} was created but its repositories could not be read.`);
      })
      .catch((cause: unknown) => {
        setError(describeError(cause));
      })
      .finally(() => {
        setBusy(false);
      });
  };

  const loading =
    repositories === null || presets === null || tones === null || brandBooks === null;

  return (
    <div className="page page--narrow">
      <PageHeader
        back={{ href: '/content', label: 'Content' }}
        title="New content"
        subtitle="A brief, the brand book it is made in, the code it draws on, and the formats to produce. Planning starts on the next page."
      />
      {request !== null && (
        <Notice kind={request.status === 'open' ? 'info' : 'warn'}>
          {request.status === 'open'
            ? `Made for the ads agent's creative request "${request.concept}". The brief, kind and formats are filled in from it; creating the content marks the request started.`
            : `The ads agent's creative request "${request.concept}" is already ${request.status}; this content will not be linked to it.`}
        </Notice>
      )}

      <div className="grid grid--form">
        <form className="panel" onSubmit={submit}>
          <div className="panel__body form">
            {loading ? (
              <Skeleton lines={6} />
            ) : (
              <>
                <div className="field">
                  <span className="field__label">Kind</span>
                  <div className="field__actions">
                    {(['video', 'image'] as const).map((option) => (
                      <label className="radio" key={option}>
                        <input
                          type="radio"
                          name="content-kind"
                          value={option}
                          checked={kind === option}
                          onChange={() => chooseKind(option)}
                        />
                        {option === 'video' ? 'Video' : 'Image'}
                      </label>
                    ))}
                  </div>
                  <p className="field__hint">
                    {kind === 'video'
                      ? 'A clip: storyboard, voiceover, music, captions and a render per format.'
                      : 'A still: one composition per format, rendered as an image.'}
                  </p>
                </div>

                <div className="field">
                  <label className="field__label" htmlFor="content-name">
                    Name
                  </label>
                  <input
                    id="content-name"
                    className="field__input"
                    value={name}
                    onChange={(event) => setName(event.target.value)}
                    placeholder="launch-reel"
                    autoComplete="off"
                    spellCheck={false}
                    autoFocus
                    maxLength={MAX_NAME_LENGTH}
                    aria-describedby="content-name-hint"
                  />
                  <p className="field__hint" id="content-name-hint">
                    Letters, numbers, hyphens and underscores. It becomes the workspace directory,
                    the container name and the URL.
                  </p>
                </div>

                <div className="field">
                  <label className="field__label" htmlFor="content-brand-book">
                    Brand book
                  </label>
                  <div className="field__pair">
                    <select
                      id="content-brand-book"
                      className="field__input"
                      value={brandBookId}
                      onChange={(event) => setBrandBookId(event.target.value)}
                      required
                      aria-describedby="content-brand-book-hint"
                    >
                      <option value="" disabled>
                        Choose a brand book…
                      </option>
                      {(brandBooks ?? []).map((book) => (
                        <option key={book.id} value={book.id}>
                          {book.name} — {brandBookSource(book)} ·{' '}
                          {brandBookStatusLabel(book, startingBooks.has(book.id))}
                        </option>
                      ))}
                    </select>
                    <button
                      type="button"
                      className="button button--quiet"
                      onClick={() => setCreatingBook(true)}
                    >
                      <Icon name="plus" />
                      New brand book…
                    </button>
                  </div>
                  {chosenBook !== null && (
                    <BrandBookNotice book={chosenBook} extracting={chosenExtracting} />
                  )}
                  <p className="field__hint" id="content-brand-book-hint">
                    The one brand this content is made in: its profile, corrections, marketing
                    context, product facts and context items are what planning and production
                    read. Manage them on{' '}
                    <Link className="link" href="/brand-books">
                      Brand books
                    </Link>
                    .
                  </p>
                </div>

                <CampaignField id="content-campaign" choice={campaignChoice} hint={wording.CAMPAIGN_FIELD_HINT} />

                <div className="field">
                  <label className="field__label" htmlFor="content-due-on">
                    {wording.NEW_CONTENT_DUE}
                  </label>
                  <input
                    id="content-due-on"
                    className="field__input"
                    type="date"
                    value={dueOn}
                    onChange={(event) => setDueOn(event.target.value)}
                    aria-describedby="content-due-on-hint"
                  />
                  <p className="field__hint" id="content-due-on-hint">
                    {wording.NEW_CONTENT_DUE_HINT}
                  </p>
                </div>

                <div className="field">
                  <span className="field__label">Code sources</span>
                  {usable.length === 0 ? (
                    <Notice kind="info">
                      No repository has a deploy key yet, so this content has no code to read
                      product moments and screens from.{' '}
                      <Link className="link" href="/repositories">
                        Add one
                      </Link>{' '}
                      to give it some.
                    </Notice>
                  ) : (
                    <ul className="choices">
                      {usable.map((repository) => (
                        <RepositoryChoice
                          key={repository.id}
                          repository={repository}
                          position={chosenRepositories.indexOf(repository.id)}
                          onToggle={() => toggleRepository(repository.id)}
                          onMakePrimary={() => makePrimary(repository.id)}
                        />
                      ))}
                    </ul>
                  )}
                  <p className="field__hint">
                    Repositories supply product moments and screens — what the product does and
                    looks like in use — not the brand, which comes from the brand book alone. The
                    first one picked is the primary, what the content is about; the rest are read
                    for context. Every clone is read-only. Optional.
                  </p>
                </div>

                <div className="field">
                  <span className="field__label">Format presets</span>
                  <ul className="choices">
                    {offered.map((preset) => (
                      <li className="choice" key={preset.preset}>
                        <label className="choice__label">
                          <input
                            type="checkbox"
                            checked={formats.includes(preset.preset)}
                            onChange={() => toggleFormat(preset.preset)}
                          />
                          <span className="choice__main">
                            <span className="choice__title">{preset.preset}</span>
                            <span className="choice__summary mono">
                              {describeFormatPreset(preset)}
                            </span>
                            <span className="choice__summary">{preset.platforms.join(', ')}</span>
                          </span>
                        </label>
                      </li>
                    ))}
                  </ul>
                  <p className="field__hint">
                    One render per preset, laid out to its own safe area and minimum type sizes.
                  </p>
                </div>

                <div className="field">
                  <span className="field__label" id="content-tone-label">
                    Tone
                  </span>
                  {/* One card per tone: its name, what it feels like and where
                      it fits. The radio stays a real input inside the label,
                      so arrow keys and a screen reader treat it as a group. */}
                  <ul
                    className="choices"
                    role="radiogroup"
                    aria-labelledby="content-tone-label"
                    aria-describedby="content-tone-hint"
                  >
                    {tones.map((option) => (
                      <li className="choice choice--card" key={option.tone}>
                        <label className="choice__label">
                          <input
                            type="radio"
                            name="content-tone"
                            value={option.tone}
                            checked={tone === option.tone}
                            onChange={() => setTone(option.tone)}
                          />
                          <span className="choice__main">
                            <span className="choice__title">
                              {option.label ?? option.tone}
                              {defaults?.defaultTone === option.tone && <Badge>default</Badge>}
                            </span>
                            <span className="choice__summary">{option.summary}</span>
                            {option.bestFor !== undefined && (
                              <span className="choice__meta">Best for: {option.bestFor}</span>
                            )}
                          </span>
                        </label>
                      </li>
                    ))}
                  </ul>
                  <p className="field__hint" id="content-tone-hint">
                    The tone decides how the content sounds and how fast it moves. You can refine it
                    in Creative direction below.
                  </p>
                </div>

                <LanguageField
                  id="content-language"
                  choice={languageChoice}
                  languages={languages}
                  hint={LANGUAGE_HINT}
                />

                <div className="field">
                  <span className="field__label">
                    {kind === 'video' ? 'Length and tracks' : 'Watermark'}
                  </span>
                  {kind === 'video' && (
                    <>
                      <input
                        id="content-seconds"
                        className="field__input field__input--narrow"
                        type="number"
                        min={MIN_VIDEO_SECONDS}
                        max={MAX_VIDEO_SECONDS}
                        step={1}
                        value={targetSeconds}
                        onChange={(event) => setTargetSeconds(event.target.value)}
                        aria-label="Target length in seconds"
                        aria-describedby="content-seconds-hint"
                      />
                      <p className="field__hint" id="content-seconds-hint">
                        Seconds. The planner writes the storyboard to this length, and the scene
                        durations have to add up to it.
                      </p>
                    </>
                  )}
                  <ul className="choices">
                    {TOGGLES.filter((toggle) => kind === 'video' || !toggle.video).map((toggle) => (
                      <li className="choice" key={toggle.field}>
                        <label className="choice__label">
                          <input
                            type="checkbox"
                            checked={toggles[toggle.field]}
                            onChange={() => {
                              setToggles((current) => ({
                                ...current,
                                [toggle.field]: !current[toggle.field],
                              }));
                            }}
                          />
                          <span className="choice__main">
                            <span className="choice__title">{toggle.label}</span>
                            <span className="choice__summary">{toggle.hint}</span>
                          </span>
                        </label>
                      </li>
                    ))}
                  </ul>
                  <p className="field__hint">
                    Pre-filled from{' '}
                    <Link className="link" href="/settings#defaults">
                      Settings &rarr; Defaults
                    </Link>
                    , and yours to change for this content alone. The planning conversation is
                    told which of them are on, so it plans no step against one you switched off.
                  </p>
                </div>

                <div className="field">
                  <label className="field__label" htmlFor="content-direction">
                    Creative direction <span className="muted">— optional</span>
                  </label>
                  <input
                    id="content-direction"
                    className="field__input"
                    value={creativeDirection}
                    onChange={(event) => setCreativeDirection(event.target.value)}
                    placeholder="Dry, almost no music, let the product do the talking."
                    autoComplete="off"
                  />
                  <p className="field__hint">
                    Refines the tone in your own words. It never replaces the tone&rsquo;s numbers.
                  </p>
                </div>

                <div className="field">
                  <label className="field__label" htmlFor="content-brief">
                    Brief
                  </label>
                  <textarea
                    id="content-brief"
                    className="field__input field__textarea"
                    rows={6}
                    value={brief}
                    onChange={(event) => setBrief(event.target.value)}
                    placeholder="What this is for, who it is aimed at, and what it has to say."
                  />
                  <p className="field__hint">
                    The planning conversation starts from this. Markdown is fine.
                  </p>
                </div>

                <details className="disclosure">
                  <summary className="disclosure__summary">
                    <Icon name="chevron-right" />
                    Brief details (optional)
                  </summary>
                  <div className="disclosure__body">
                    <p className="field__hint">
                      The marketing side of the piece: what it is for, who it is aimed at and
                      where it goes. Editable later on the content page.
                    </p>
                    <BriefDetailsFields
                      value={briefDetails}
                      onChange={setBriefDetails}
                      disabled={busy}
                    />
                  </div>
                </details>

                {kind === 'video' && (
                  <div className="field">
                    <label className="field__label" htmlFor="content-video-model">
                      Video model <span className="muted">&mdash; optional</span>
                    </label>
                    {catalogue !== null && catalogue.models.length > 0 ? (
                      <select
                        id="content-video-model"
                        className="field__input"
                        value={videoModel}
                        onChange={(event) => setVideoModel(event.target.value)}
                      >
                        <option value="">
                          This install&rsquo;s model ({catalogue.model})
                        </option>
                        {catalogue.models.map((listing) => (
                          <option key={listing.model} value={listing.model}>
                            {listing.name} &mdash;{' '}
                            {listing.usdPerSecond === null
                              ? `priced per ${listing.priceUnit}`
                              : `$${listing.usdPerSecond.toFixed(4)} a second`}
                          </option>
                        ))}
                      </select>
                    ) : (
                      <input
                        id="content-video-model"
                        className="field__input"
                        value={videoModel}
                        onChange={(event) => setVideoModel(event.target.value)}
                        placeholder={catalogue?.model ?? 'This install’s model'}
                        autoComplete="off"
                      />
                    )}
                    <p className="field__hint">
                      Generated clips run on this model instead of the install&rsquo;s, and it
                      wins over a model the plan names &mdash; which is how two contents compare
                      two models on one brief. Leave it as it is to follow the plan and Settings.
                      {catalogue !== null &&
                        catalogue.source !== 'live' &&
                        catalogue.source !== 'cache' &&
                        ' The list could not be read from OpenRouter just now, so it is this build’s own price sheet.'}
                    </p>
                  </div>
                )}

                <div className="field__row">
                  <div className="field">
                    <label className="field__label" htmlFor="content-spend-cap">
                      Spend cap (USD)
                    </label>
                    <input
                      id="content-spend-cap"
                      className="field__input"
                      type="number"
                      min={0}
                      max={MAX_SPEND_CAP_USD}
                      step="0.01"
                      inputMode="decimal"
                      value={spendCap}
                      onChange={(event) => setSpendCap(event.target.value)}
                      placeholder="No cap"
                    />
                    <p className="field__hint">
                      From Settings, and editable per content. A provider call that would cross it
                      is refused. Clear the field for no cap of its own.
                    </p>
                  </div>

                  <div className="field">
                    <label className="checkbox">
                      <input
                        type="checkbox"
                        checked={schedule}
                        onChange={(event) => setSchedule(event.target.checked)}
                      />
                      Start production at a set time
                    </label>
                    {schedule && (
                      <div className="field__pair">
                        <input
                          className="field__input"
                          type="date"
                          value={day}
                          onChange={(event) => setDay(event.target.value)}
                          aria-label="Day production starts"
                        />
                        <input
                          className="field__input field__input--narrow"
                          type="text"
                          inputMode="numeric"
                          placeholder="HH:mm"
                          maxLength={5}
                          value={time}
                          onChange={(event) => setTime(event.target.value)}
                          onBlur={() => {
                            const tidy = normaliseTime(time);
                            if (tidy !== null) setTime(tidy);
                          }}
                          aria-label="Time production starts, 24-hour clock"
                          aria-invalid={time !== '' && normaliseTime(time) === null}
                        />
                      </div>
                    )}
                    <p className="field__hint">
                      {schedule
                        ? 'Your timezone, 24-hour clock. The plan has to be marked ready by then, or the start is missed.'
                        : 'Leave off to start production by hand once the plan is ready.'}
                    </p>
                  </div>
                </div>

                {error !== null && <Notice kind="error">{error}</Notice>}
                {stderr !== null && <pre className="output">{stderr}</pre>}

                <div className="field__actions">
                  <button
                    type="submit"
                    className="button button--primary"
                    disabled={
                      busy || trimmedName === '' || formats.length === 0 || chosenBook === null
                    }
                  >
                    <Icon name="plus" />
                    {busy ? 'Creating and cloning…' : 'Create content'}
                  </button>
                  <Link className="button button--quiet" href="/content">
                    Cancel
                  </Link>
                </div>
                {busy && (
                  <Loader
                    label={
                      chosenRepositories.length === 0
                        ? 'Creating the content…'
                        : `Creating the content and cloning ${String(chosenRepositories.length)} ${
                            chosenRepositories.length === 1 ? 'repository' : 'repositories'
                          }…`
                    }
                    {...(chosenRepositories.length === 0
                      ? {}
                      : { detail: 'Each clone is shallow; the content page opens once they are all in.' })}
                  />
                )}
              </>
            )}
          </div>
        </form>

        <Panel title="What happens next" icon="info" className="panel--aside">
          <ol className="steps steps--plain">
            <li className="step">
              <span className="step__marker">1</span>
              <div className="step__main">
                <span className="step__title">Set up</span>
                <span className="step__body">
                  A container starts and every repository is cloned into it, shallow and read-only,
                  with its own deploy key.
                </span>
              </div>
            </li>
            <li className="step">
              <span className="step__marker">2</span>
              <div className="step__main">
                <span className="step__title">Plan</span>
                <span className="step__body">
                  Claude reads the brand book and the clones, asks what it still needs to know, and
                  writes the plan as steps. A brand book still extracting is waited for first.
                </span>
              </div>
            </li>
            <li className="step">
              <span className="step__marker">3</span>
              <div className="step__main">
                <span className="step__title">Produce</span>
                <span className="step__body">
                  Mark it ready. One agent per step, inside the spend cap, until every format is
                  rendered.
                </span>
              </div>
            </li>
            <li className="step">
              <span className="step__marker">4</span>
              <div className="step__main">
                <span className="step__title">Review</span>
                <span className="step__body">
                  You watch the render, read the share copy, and approve it or send it back with
                  notes.
                </span>
              </div>
            </li>
          </ol>
        </Panel>
      </div>

      {creatingBook && (
        <NewBrandBookDialog onCreated={onBookCreated} onCancel={() => setCreatingBook(false)} />
      )}
    </div>
  );
}

/** The status as the select's option text can carry it: words, no badge. */
function brandBookStatusLabel(book: BrandBookSummary, starting: boolean): string {
  if (starting || book.brandStatus === 'extracting') return 'extracting';
  if (book.brandStatus === 'failed') return 'failed';
  if (book.brandStatus === 'ready') return 'ready';
  return 'no profile';
}

/**
 * What choosing this book means for the plan. A failed or still-extracting
 * book can be chosen — the content is not held back by its brand — but the
 * operator is told what planning will do about it.
 */
function BrandBookNotice({
  book,
  extracting,
}: {
  readonly book: BrandBookSummary;
  readonly extracting: boolean;
}) {
  if (extracting) {
    return (
      <Notice kind="wait">
        <strong>{book.name}</strong> is extracting its brand profile. You can create the content
        now; planning waits for the extraction to finish before it starts.
      </Notice>
    );
  }
  if (book.brandStatus === 'failed') {
    return (
      <Notice kind="warn">
        The last extraction of <strong>{book.name}</strong> failed
        {book.brandError === null ? '' : `: ${book.brandError}`}. The plan will run on an empty
        brand profile unless you{' '}
        <Link className="link" href={brandBookPath(book.id)}>
          refresh or correct it
        </Link>{' '}
        first.
      </Notice>
    );
  }
  if (book.brandStatus === 'none') {
    return (
      <Notice kind="info">
        <strong>{book.name}</strong> has no brand profile yet. One is extracted when the content is
        created, and planning waits for it.
      </Notice>
    );
  }
  return null;
}
